/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { Controller, ControllerProps, UseFormSetValue, UseFormResetField, UseFormRegisterReturn } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import { AUTH_STRINGS } from '../../../constants/strings';
import { Logger } from '../../../libs/utils/logger';
import { RegisterComponent } from '../../../libs/saga/dataSaga';
import { convertLocalDateToUTC } from '../../Utils/CommonBaseClass';

Logger.debug("FxDateEdit.tsx", "Fx Date Edit initializing")
/**
 * Component used to edit date 
 */

type TFieldValues = Record<string, string | null>;

type FxDateEditPropsType = ControllerProps & DatePickerProps<any, any>;

interface FxDateEditProps extends Partial<FxDateEditPropsType>{
    id?: string;
    setValue: UseFormSetValue<TFieldValues>;
    resetField?: UseFormResetField<TFieldValues>;
    register?: UseFormRegisterReturn;
    name: string;
    rules?: any;
    onChange?:(event:any, name: any) =>void;
    required?: boolean;
    month?: any;
    day?: any;
    year?: any;
    variant?: string;
    type?: string;
    defaultCalendarMonth?:any
    ageLimitMessage?: string;
    futureDateMessage?:string;
    defaultValue?: any;
    allowUTCMaxDate?: boolean;
}

export const FxDateEdit: React.FC<FxDateEditProps> =
  (props) => {
    ({ props } = RegisterComponent(props));
    const [errMessage, setErrMessage] = useState<string>('');

    /**
     * Effective maxDate: when `disableFuture` and `allowUTCMaxDate` are both set,
     * cap at UTC today. If the caller also passed a `maxDate`, the earlier of
     * the two wins so the UTC cap can never be loosened.
     */
    const effectiveMaxDate = React.useMemo(() => {
      const userMax = props?.maxDate ? moment(props.maxDate) : undefined;
      if (props?.disableFuture && props?.allowUTCMaxDate) {
        const utcCap = moment(convertLocalDateToUTC(moment()), 'MM/DD/YYYY');
        if (!userMax) return utcCap;
        return userMax.isBefore(utcCap) ? userMax : utcCap;
      }
      return userMax;
    }, [props?.maxDate, props?.disableFuture, props?.allowUTCMaxDate]);

    /**
     * This useEffect basically sets the value passed to this component using setValue
     */
    useEffect(() => {
      if (props?.value) {
        props?.setValue(props?.name, props?.value);
      }
    }, [props?.value, props?.setValue]);

    /**
     * handle change method to set value
     * @param date :value
     * @param name :field name
     */
    const handleDateChange = (date: any, name: any) => {
      let formattedValue: string | null = null;
    
      const isValidDate = date && date.isValid && date.isValid();
      const dateIsAfterMaxDate = isValidDate && effectiveMaxDate && date.isAfter(effectiveMaxDate, 'day');
    
      if (!isValidDate) {
        setErrMessage(AUTH_STRINGS.ERRORS.DATE_INVALID_REGEXP);
        formattedValue = null;
      } else if (dateIsAfterMaxDate) {
        formattedValue = moment(date.$d).format('MM/DD/YYYY');
        setErrMessage(props.ageLimitMessage || AUTH_STRINGS.ERRORS.DATE_IS_AFTER_MAX_ALLOWED);
      } else {
        formattedValue = moment(date?.$d).format("MM/DD/YYYY");
        setErrMessage('');
      }
      props.setValue(props.name, formattedValue, { shouldValidate: true });
      props.onChange?.(formattedValue, name);
    };

    const validateDate = (value: any) => {
      const isRequired = props.required || (props.rules && props.rules.required);
      if (!value) {
        return isRequired ? AUTH_STRINGS.ERRORS.DATE_INVALID_REGEXP : true;
      }
      const date = moment(value?.toDate?.() || value);
      const today = moment();
      if (!date.isValid()) {
        return AUTH_STRINGS.ERRORS.DATE_INVALID_REGEXP;
      }
      const isAfterToday = date.isAfter(today, 'day');
      const isAfterMaxDate = effectiveMaxDate && date.isAfter(effectiveMaxDate, 'day');

      if (isAfterMaxDate&&!isAfterToday) {
        return props.ageLimitMessage || AUTH_STRINGS.ERRORS.DATE_IS_AFTER_MAX_ALLOWED;
      }
    
      if (isAfterToday) {
        return props.futureDateMessage || AUTH_STRINGS.ERRORS.FUTURE_DATE;
      }
      return true;
    };


    return (
      <Controller
        name={props.name}
        control={props.control ? props.control : undefined}
        rules={{
          ...(props.rules ? props.rules : { required: false }),
          validate: props?.rules?.validate || validateDate
        }}
        defaultValue={props?.defaultValue}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={props?.label}
              value={value ? moment(value, "MM/DD/YYYY", true) : null}
              onChange={onChange = (newValue) => {
                handleDateChange(newValue, props?.name);
              }}
              className={props?.className||'fx-input-edit'}
              defaultCalendarMonth={props?.defaultCalendarMonth ? moment(props?.defaultCalendarMonth) : undefined}
              maxDate={effectiveMaxDate} //maxDate={ageLimit(18)}
              minDate={props?.minDate ? moment(props?.minDate) : undefined} //minDate={new Date()}
              inputFormat={props?.month ? "MM/YYYY" : props?.day ? 'DD' : props?.year ? 'YYYY' : "MM/DD/YYYY"}
              views={props?.month ? ['year', 'month'] : props?.day ? ['day'] : props?.year ? ['year'] : ['year', 'month', 'day']}
              renderInput={(params: any) => <TextField {...params} InputLabelProps={{
                shrink: true
              }}
                inputProps={{
                  ...params.inputProps,
                  placeholder: props?.month ? "MM/YYYY" : props?.day ? 'DD' : props?.year ? 'YYYY' : "MM/DD/YYYY"
                }}
                required={props?.required}
              />}
              disablePast={props?.disablePast}
              disableFuture={props?.disableFuture}
              disabled={props?.disabled}
            />
          </LocalizationProvider>
        )}
      />
    );
  }